import type { OnboardingSector } from '../onboarding/sectoral-defaults';
import { SECTORAL_DEFAULTS } from '../onboarding/sectoral-defaults';
import { SECTOR_ALT_PALETTES, SECTORAL_PALETTE_THEME } from '../onboarding/sectoral-palette-theme';
import { COMPONENT_SCHEMA_REGISTRY } from './ComponentSchemaRegistry';
import { GoogleFontFamily, type WebsiteColors, type WebsiteStyles } from './Styles';
import {
  COMPONENT_IDS,
  isComponentRef,
  type ComponentId,
  type ComponentInstance,
  type PageComponent,
  type WebsiteConfig,
  type WebsitePageInstance,
} from './WebsiteTypes';

/**
 * Onboarding v4, lot 7 (écrans 16 bis, 23, 25) : « un style = un template complet ». Quatre presets
 * de configuration, pas de moteur de templates : chaque preset est une composition de composants
 * EXISTANTS du site (ids et variantes de `ComponentSchemas.ts`) + un `WebsiteStyles` partiel
 * (typographies, bordures, décor, espacement) + une palette par secteur. `applyStylePreset`
 * (fonction pure, testée) applique le tout à un `WebsiteConfig` en GARDANT les textes et images
 * déjà présents. Le choix est mémorisé dans `config.template`.
 */
export const WEBSITE_STYLE_PRESET_IDS = ['chic', 'doux', 'audacieux', 'minimal'] as const;
export type WebsiteStylePresetId = (typeof WEBSITE_STYLE_PRESET_IDS)[number];

export const isWebsiteStylePresetId = (value: unknown): value is WebsiteStylePresetId =>
  typeof value === 'string' && (WEBSITE_STYLE_PRESET_IDS as readonly string[]).includes(value);

/** Conditions d'inclusion d'un bloc du preset (règles communes de la spec, section 16 bis). */
export type StylePresetBlockCondition = 'google_reviews' | 'no_google_reviews' | 'video' | 'no_video';

export interface StylePresetBlock {
  componentId: ComponentId;
  variant: string;
  /** Valeurs par défaut du preset ; une valeur déjà présente dans la config gagne toujours. */
  values?: Record<string, unknown>;
  when?: StylePresetBlockCondition;
}

export interface WebsiteStylePreset {
  id: WebsiteStylePresetId;
  /** Clé i18n du nom (namespace `PROFESSIONAL_APP_WEBSITE_STYLES`). */
  label_key: string;
  description_key: string;
  styles: Omit<WebsiteStyles, 'colors'>;
  /** Clé `palette_*` (cf. `SECTORAL_PALETTE_THEME`) pour un secteur donné. */
  palette: (sector: OnboardingSector) => string;
  header_variant: 'classic' | 'hamburger';
  footer_variant: 'classic' | 'minimal' | 'centered';
  home: StylePresetBlock[];
  services_variant: 'grid' | 'list';
  /** Page « Galerie » (photo-gallery) ajoutée quand le pro a assez de photos. */
  gallery_page?: { variant: 'grid' | 'masonry' | 'carousel'; min_photos: number };
}

/**
 * Textes de composition posés par les presets (titres de sections, libellés de boutons). Contenu
 * de site en français, comme les banques sectorielles : le pro les réécrit dans l'éditeur.
 */
export const STYLE_PRESET_CONTENT = {
  highlight_title: 'Incontournables',
  specialties_title: 'Ce qui fait la différence',
  reviews_title: 'Avis clients',
  about_title: 'Histoire et savoir-faire',
  faq_title: 'Questions fréquentes',
  gallery_title: 'Galerie',
  gallery_page_title: 'Galerie',
  gallery_page_slug: '/galerie',
  results_title: 'Avant / après',
  places_page_title: 'Zones desservies',
  places_page_slug: '/zones',
  cta_text: 'Prenez rendez-vous en quelques secondes, à l’heure qui vous arrange.',
  cta_book: 'Réserver',
  cta_services: 'Voir les prestations',
  // Newsletter sur l'accueil (retour recette 02/11/2026) : présente dans les quatre styles.
  newsletter_title: 'Restez informé',
  newsletter_description: 'Recevez les nouveautés et les offres du moment, directement par e-mail.',
  newsletter_button: 'S’abonner',
  newsletter_consent: 'En vous inscrivant, vous acceptez de recevoir des communications par e-mail.',
} as const;

const sectorPalette = (sector: OnboardingSector): string =>
  (SECTORAL_DEFAULTS[sector] ?? SECTORAL_DEFAULTS.generic).palette;
const altPalette = (sector: OnboardingSector, index: 0 | 1): string =>
  (SECTOR_ALT_PALETTES[sector] ?? SECTOR_ALT_PALETTES.generic)[index];
/** Secteurs dont le thème par défaut est déjà sombre : « Audacieux » garde ce thème. */
const DARK_SECTOR_THEMES: ReadonlySet<string> = new Set(['palette_vtc', 'palette_coach_sport']);

const CTA_SINGLE: StylePresetBlock = {
  componentId: COMPONENT_IDS.CALL_TO_ACTION,
  variant: 'single',
  values: { text: STYLE_PRESET_CONTENT.cta_text, primaryLabel: STYLE_PRESET_CONTENT.cta_book },
};
const CTA_DOUBLE: StylePresetBlock = {
  componentId: COMPONENT_IDS.CALL_TO_ACTION,
  variant: 'double',
  values: {
    text: STYLE_PRESET_CONTENT.cta_text,
    primaryLabel: STYLE_PRESET_CONTENT.cta_book,
    secondaryLabel: STYLE_PRESET_CONTENT.cta_services,
  },
};
/**
 * Inscription à la newsletter, juste avant l'appel à l'action final : un visiteur qui ne réserve pas
 * tout de suite laisse au moins son e-mail. Les textes sont les valeurs par défaut du bloc (le pro
 * les retouche dans l'éditeur) ; `tagId` reste vide, la liste par défaut du pro suffit.
 */
const NEWSLETTER: StylePresetBlock = {
  componentId: COMPONENT_IDS.NEWSLETTER_SUBSCRIPTION,
  variant: 'horizontal',
  values: {
    title: STYLE_PRESET_CONTENT.newsletter_title,
    description: STYLE_PRESET_CONTENT.newsletter_description,
    buttonLabel: STYLE_PRESET_CONTENT.newsletter_button,
    consentText: STYLE_PRESET_CONTENT.newsletter_consent,
  },
};
const GOOGLE_REVIEWS = (variant: 'carousel' | 'grid' | 'wall'): StylePresetBlock => ({
  componentId: COMPONENT_IDS.GOOGLE_REVIEWS,
  variant,
  values: { title: STYLE_PRESET_CONTENT.reviews_title },
  when: 'google_reviews',
});
const TESTIMONIALS = (variant: 'carousel' | 'grid' | 'wall' | 'highlight'): StylePresetBlock => ({
  componentId: COMPONENT_IDS.TESTIMONIALS,
  variant,
  values: { title: STYLE_PRESET_CONTENT.reviews_title },
  when: 'no_google_reviews',
});

/** Tableau « Les 4 styles » de la spec, avec les ids et variantes réels du code. */
export const WEBSITE_STYLE_PRESETS: Record<WebsiteStylePresetId, WebsiteStylePreset> = {
  chic: {
    id: 'chic',
    label_key: 'PRESET_CHIC',
    description_key: 'PRESET_CHIC_DESCRIPTION',
    styles: {
      typography: { heading: GoogleFontFamily.Playfair, body: GoogleFontFamily.Lato },
      borderStyle: 'none',
      decoration: 'diamonds',
      verticalSpacing: 'airy',
    },
    palette: (sector): string => altPalette(sector, 0),
    header_variant: 'classic',
    footer_variant: 'minimal',
    home: [
      { componentId: COMPONENT_IDS.HEADER_HERO, variant: 'vertical' },
      {
        componentId: COMPONENT_IDS.PRESTATION_HIGHLIGHT,
        variant: 'list',
        values: { title: STYLE_PRESET_CONTENT.highlight_title },
      },
      {
        componentId: COMPONENT_IDS.TEXT_BLOCK,
        variant: 'with-image',
        values: { title: STYLE_PRESET_CONTENT.about_title, imagePosition: 'right' },
      },
      GOOGLE_REVIEWS('carousel'),
      TESTIMONIALS('highlight'),
      NEWSLETTER,
      CTA_SINGLE,
      { componentId: COMPONENT_IDS.CONTACT_INFO, variant: 'compact', values: { showPhone: true, showEmail: true } },
    ],
    services_variant: 'list',
  },
  doux: {
    id: 'doux',
    label_key: 'PRESET_DOUX',
    description_key: 'PRESET_DOUX_DESCRIPTION',
    styles: {
      typography: { heading: GoogleFontFamily.Raleway, body: GoogleFontFamily.Nunito },
      borderStyle: 'rounded',
      decoration: 'dots',
      verticalSpacing: 'standard',
    },
    palette: sectorPalette,
    header_variant: 'classic',
    footer_variant: 'centered',
    home: [
      { componentId: COMPONENT_IDS.HEADER_HERO, variant: 'carousel' },
      {
        componentId: COMPONENT_IDS.SPECIALTIES,
        variant: 'tags',
        values: { title: STYLE_PRESET_CONTENT.specialties_title },
      },
      {
        componentId: COMPONENT_IDS.PRESTATION_HIGHLIGHT,
        variant: 'grid',
        values: { title: STYLE_PRESET_CONTENT.highlight_title },
      },
      {
        componentId: COMPONENT_IDS.PHOTO_GALLERY,
        variant: 'masonry',
        values: { title: STYLE_PRESET_CONTENT.gallery_title },
      },
      GOOGLE_REVIEWS('grid'),
      TESTIMONIALS('highlight'),
      NEWSLETTER,
      CTA_DOUBLE,
    ],
    services_variant: 'grid',
    gallery_page: { variant: 'masonry', min_photos: 4 },
  },
  audacieux: {
    id: 'audacieux',
    label_key: 'PRESET_AUDACIEUX',
    description_key: 'PRESET_AUDACIEUX_DESCRIPTION',
    styles: {
      typography: { heading: GoogleFontFamily.Poppins, body: GoogleFontFamily.Poppins },
      borderStyle: 'none',
      decoration: 'lines',
      verticalSpacing: 'tight',
    },
    palette: (sector): string => {
      const own = sectorPalette(sector);
      return DARK_SECTOR_THEMES.has(own) ? own : altPalette(sector, 1);
    },
    header_variant: 'hamburger',
    footer_variant: 'classic',
    home: [
      { componentId: COMPONENT_IDS.HEADER_HERO, variant: 'video', when: 'video' },
      { componentId: COMPONENT_IDS.HEADER_HERO, variant: 'horizontal', when: 'no_video' },
      { componentId: COMPONENT_IDS.KEY_FIGURES, variant: 'banner' },
      {
        componentId: COMPONENT_IDS.PRESTATION_HIGHLIGHT,
        variant: 'grid',
        values: { title: STYLE_PRESET_CONTENT.highlight_title },
      },
      {
        componentId: COMPONENT_IDS.BEFORE_AFTER,
        variant: 'slider',
        values: { title: STYLE_PRESET_CONTENT.results_title },
      },
      GOOGLE_REVIEWS('wall'),
      TESTIMONIALS('wall'),
      NEWSLETTER,
      CTA_SINGLE,
    ],
    services_variant: 'grid',
  },
  minimal: {
    id: 'minimal',
    label_key: 'PRESET_MINIMAL',
    description_key: 'PRESET_MINIMAL_DESCRIPTION',
    styles: {
      typography: { heading: GoogleFontFamily.Inter, body: GoogleFontFamily.Inter },
      borderStyle: 'soft',
      decoration: 'none',
      verticalSpacing: 'airy',
    },
    palette: (): string => 'palette_photographe',
    header_variant: 'classic',
    footer_variant: 'minimal',
    home: [
      { componentId: COMPONENT_IDS.HEADER_HERO, variant: 'horizontal' },
      { componentId: COMPONENT_IDS.PRESTATION_LISTING, variant: 'list' },
      {
        componentId: COMPONENT_IDS.TEXT_BLOCK,
        variant: 'single-column',
        values: { title: STYLE_PRESET_CONTENT.about_title },
      },
      { componentId: COMPONENT_IDS.FAQ, variant: 'collapsible', values: { title: STYLE_PRESET_CONTENT.faq_title } },
      NEWSLETTER,
      { componentId: COMPONENT_IDS.CONTACT_INFO, variant: 'compact', values: { showPhone: true, showEmail: true } },
    ],
    services_variant: 'list',
  },
};

/** Couleurs d'un preset pour un secteur (sans couleurs de marque). */
export const presetPaletteKey = (presetId: WebsiteStylePresetId, sector: OnboardingSector): string =>
  WEBSITE_STYLE_PRESETS[presetId].palette(sector);

export const presetColors = (presetId: WebsiteStylePresetId, sector: OnboardingSector): WebsiteColors =>
  (SECTORAL_PALETTE_THEME[presetPaletteKey(presetId, sector)] ?? SECTORAL_PALETTE_THEME.palette_generic).colors;

export interface ApplyStylePresetOptions {
  /** Fiche Google reliée et avis exploitables : `google-reviews` remplace les témoignages. */
  hasGoogleReviews?: boolean;
  /** Une vidéo de présentation existe (sinon la variante vidéo du héros retombe sur `horizontal`). */
  hasVideo?: boolean;
  /** Au moins un lieu « en déplacement » : page zones desservies avec `area-list`. */
  hasTravelZone?: boolean;
  /** Au moins une actualité publiée : `news-highlight` sur l'accueil. */
  hasPublishedNews?: boolean;
  /** Clés S3 des photos du pro (galerie, carrousel du héros, image de l'à-propos). */
  photoKeys?: string[];
  /** Couleurs de marque (logo) ; sans elles, la palette du preset pour le secteur. */
  brandColors?: WebsiteColors;
  /** Horodatage posé dans `updateDate` (injecté pour rester pur). */
  now?: string;
}

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0);

const nonEmptyEntries = (values: Record<string, unknown> | undefined): Record<string, unknown> =>
  Object.fromEntries(Object.entries(values ?? {}).filter(([, value]): boolean => !isEmptyValue(value)));

/** Champs requis d'une variante (registre des composants) — vide pour une variante inconnue. */
const requiredFields = (componentId: ComponentId, variant: string): string[] => {
  const variants = COMPONENT_SCHEMA_REGISTRY[componentId]?.meta.variants as
    | Record<string, { required?: readonly string[] }>
    | undefined;
  return [...(variants?.[variant]?.required ?? [])];
};

const isInstance = (component: PageComponent): component is ComponentInstance => !isComponentRef(component);

const findPage = (pages: WebsitePageInstance[], type: string): WebsitePageInstance | undefined =>
  pages.find((page): boolean => page.type === type);

/** Première instance d'un composant sur une page (hors refs). */
const findInstance = (page: WebsitePageInstance | undefined, componentId: ComponentId): ComponentInstance | undefined =>
  page?.components.filter(isInstance).find((component): boolean => component.componentId === componentId);

const pictureItems = (keys: string[], field: string): Record<string, unknown>[] =>
  keys.map((key): Record<string, unknown> => ({ [field]: { url: key } }));

/**
 * Valeurs d'un bloc du preset : défauts du preset, écrasés par ce que la config portait déjà pour
 * le même composant (accueil d'abord, puis n'importe quelle page), complétés par la matière du
 * pro (photos). Renvoie `null` quand un champ requis reste vide : le bloc n'est pas posé (« aucun
 * composant vide publié »).
 */
const buildBlockValues = (
  block: StylePresetBlock,
  existing: ComponentInstance | undefined,
  ctx: { photoKeys: string[]; bookingHref: string; servicesHref: string },
): { values: Record<string, unknown>; variant: string } | null => {
  const values: Record<string, unknown> = { ...(block.values ?? {}), ...nonEmptyEntries(existing?.values) };
  let variant = block.variant;
  switch (block.componentId) {
    case COMPONENT_IDS.HEADER_HERO:
      if (isEmptyValue(values.title)) values.title = '{{pro:name}}';
      if (variant === 'carousel' && isEmptyValue(values.imagesUrl) && ctx.photoKeys.length >= 2)
        values.imagesUrl = ctx.photoKeys.slice(0, 5).map((key): { url: string } => ({ url: key }));
      // Carrousel sans photos : le héros retombe sur la variante horizontale plutôt que de disparaître.
      if (variant === 'carousel' && isEmptyValue(values.imagesUrl)) variant = 'horizontal';
      if (variant === 'horizontal' && isEmptyValue(values.imageUrl) && ctx.photoKeys[0])
        values.imageUrl = { url: ctx.photoKeys[0] };
      break;
    case COMPONENT_IDS.CALL_TO_ACTION:
      if (isEmptyValue(values.primaryHref)) values.primaryHref = ctx.bookingHref;
      if (block.variant === 'double' && isEmptyValue(values.secondaryHref)) values.secondaryHref = ctx.servicesHref;
      break;
    case COMPONENT_IDS.PHOTO_GALLERY:
      if (isEmptyValue(values.items) && ctx.photoKeys.length >= 3)
        values.items = pictureItems(ctx.photoKeys.slice(0, 12), 'imageUrl');
      break;
    case COMPONENT_IDS.TEXT_BLOCK:
      if (block.variant === 'with-image' && isEmptyValue(values.imageUrl) && ctx.photoKeys[0])
        values.imageUrl = { url: ctx.photoKeys[0] };
      // Sans texte, un bloc « à propos » n'a rien à dire : il n'est pas posé.
      if (isEmptyValue(values.content)) return null;
      break;
    default:
      break;
  }
  for (const field of requiredFields(block.componentId, variant)) {
    if (isEmptyValue(values[field])) return null;
  }
  return { values, variant };
};

const conditionHolds = (when: StylePresetBlockCondition | undefined, options: ApplyStylePresetOptions): boolean => {
  switch (when) {
    case 'google_reviews':
      return options.hasGoogleReviews === true;
    case 'no_google_reviews':
      return options.hasGoogleReviews !== true;
    case 'video':
      return options.hasVideo === true;
    case 'no_video':
      return options.hasVideo !== true;
    default:
      return true;
  }
};

const pageHref = (page: WebsitePageInstance | undefined, fallback: string): string =>
  page ? `{{page:${page.id}}}` : fallback;

/**
 * Applique un preset à une config : nouvelle composition de l'accueil (textes et images conservés
 * par composant), variantes des composants partagés et de la liste des prestations, pages
 * ajoutées quand la matière existe (galerie, zones), thème du preset (couleurs de marque si
 * fournies), `template` mémorisé. Pure : la config d'entrée n'est pas modifiée.
 */
export function applyStylePreset(
  config: WebsiteConfig,
  presetId: WebsiteStylePresetId,
  sector: OnboardingSector,
  options: ApplyStylePresetOptions = {},
): WebsiteConfig {
  const preset = WEBSITE_STYLE_PRESETS[presetId];
  const next: WebsiteConfig = structuredClone(config);
  const pages = next.pages;
  const home = findPage(pages, 'home');
  const bookingPage = findPage(pages, 'booking');
  const servicesPage = findPage(pages, 'services');
  const contactPage = findPage(pages, 'contact');
  const aboutPage = findPage(pages, 'about');
  const photoKeys = (options.photoKeys ?? []).filter((key): boolean => typeof key === 'string' && key.length > 0);
  const ctx = {
    photoKeys,
    bookingHref: pageHref(bookingPage, pageHref(contactPage, '/')),
    servicesHref: pageHref(servicesPage, pageHref(bookingPage, '/')),
  };

  // ── Composants partagés : seule la variante change, le pro garde logo et réglages ──
  for (const shared of Object.values(next.sharedComponents ?? {})) {
    if (shared.componentId === COMPONENT_IDS.HEADER_MENU) {
      shared.variant = preset.header_variant;
      if (bookingPage) shared.values = { ...shared.values, ctaPageId: bookingPage.id };
    } else if (shared.componentId === COMPONENT_IDS.FOOTER) {
      shared.variant = preset.footer_variant;
    }
  }

  // ── Accueil : nouvelle composition, matière existante reprise par composant ──
  if (home) {
    const refs = home.components.filter((component): boolean => isComponentRef(component));
    const headerRef = refs.find((ref): boolean => ref.id.endsWith('header') || ref.id.includes('header'));
    const footerRef = refs.find((ref): boolean => ref.id.endsWith('footer') || ref.id.includes('footer'));
    const otherRefs = refs.filter((ref): boolean => ref !== headerRef && ref !== footerRef);
    const existingHome = home.components.filter(isInstance);
    const usedIds = new Set<string>();
    const built: ComponentInstance[] = [];

    for (const block of preset.home) {
      if (!conditionHolds(block.when, options)) continue;
      const fromHome = existingHome.find(
        (component): boolean => component.componentId === block.componentId && !usedIds.has(component.id),
      );
      const fromElsewhere =
        fromHome ??
        pages
          .filter((page): boolean => page !== home)
          .map((page): ComponentInstance | undefined => findInstance(page, block.componentId))
          .find((component): component is ComponentInstance => component !== undefined);
      const result = buildBlockValues(block, fromElsewhere, ctx);
      if (!result) continue;
      const id = fromHome?.id ?? `home-${block.componentId}`;
      usedIds.add(id);
      built.push({ id, componentId: block.componentId, variant: result.variant, values: result.values });
    }

    if (options.hasPublishedNews && !built.some((b): boolean => b.componentId === COMPONENT_IDS.NEWS_HIGHLIGHT)) {
      const existingNews = existingHome.find((c): boolean => c.componentId === COMPONENT_IDS.NEWS_HIGHLIGHT);
      built.push({
        id: existingNews?.id ?? `home-${COMPONENT_IDS.NEWS_HIGHLIGHT}`,
        componentId: COMPONENT_IDS.NEWS_HIGHLIGHT,
        variant: 'grid',
        values: { ...(existingNews?.values ?? {}) },
      });
    }

    home.components = [
      ...otherRefs,
      ...(headerRef ? [headerRef] : []),
      ...built,
      ...(footerRef ? [footerRef] : []),
    ] as PageComponent[];
  }

  // ── Page prestations : variante de la liste ──
  const listing = findInstance(servicesPage, COMPONENT_IDS.PRESTATION_LISTING);
  if (listing) listing.variant = preset.services_variant;

  // ── Page « À propos » : l'image du bloc texte suit le style quand elle manque ──
  const aboutText = findInstance(aboutPage, COMPONENT_IDS.TEXT_BLOCK);
  if (aboutText && presetId === 'chic' && isEmptyValue(aboutText.values.imageUrl) && photoKeys[0]) {
    aboutText.variant = 'with-image';
    aboutText.values = { ...aboutText.values, imageUrl: { url: photoKeys[0] }, imagePosition: 'left' };
  }

  // ── Pages ajoutées quand la matière existe ──
  const sharedHeaderKey = Object.entries(next.sharedComponents ?? {}).find(
    ([, c]): boolean => c.componentId === COMPONENT_IDS.HEADER_MENU,
  )?.[0];
  const sharedFooterKey = Object.entries(next.sharedComponents ?? {}).find(
    ([, c]): boolean => c.componentId === COMPONENT_IDS.FOOTER,
  )?.[0];
  const wrapWithShared = (pageId: string, blocks: ComponentInstance[]): PageComponent[] => [
    ...(sharedHeaderKey ? [{ id: `${pageId}-header`, ref: sharedHeaderKey }] : []),
    ...blocks,
    ...(sharedFooterKey ? [{ id: `${pageId}-footer`, ref: sharedFooterKey }] : []),
  ];
  const maxMenuOrder = pages.reduce((max, page): number => Math.max(max, page.menuOrder ?? 0), 0);

  if (
    preset.gallery_page &&
    photoKeys.length >= preset.gallery_page.min_photos &&
    !pages.some((page): boolean => page.slug === STYLE_PRESET_CONTENT.gallery_page_slug)
  ) {
    pages.push({
      id: 'page_gallery',
      type: 'custom',
      slug: STYLE_PRESET_CONTENT.gallery_page_slug,
      title: STYLE_PRESET_CONTENT.gallery_page_title,
      showInMenu: true,
      menuOrder: maxMenuOrder + 1,
      components: wrapWithShared('page_gallery', [
        {
          id: 'gallery-photos',
          componentId: COMPONENT_IDS.PHOTO_GALLERY,
          variant: preset.gallery_page.variant,
          values: {
            title: STYLE_PRESET_CONTENT.gallery_title,
            items: pictureItems(photoKeys.slice(0, 24), 'imageUrl'),
          },
        },
      ]),
    });
  }

  if (options.hasTravelZone && !pages.some((page): boolean => page.type === 'places')) {
    pages.push({
      id: 'page_places',
      type: 'places',
      slug: STYLE_PRESET_CONTENT.places_page_slug,
      title: STYLE_PRESET_CONTENT.places_page_title,
      showInMenu: true,
      menuOrder: maxMenuOrder + 2,
      components: wrapWithShared('page_places', [
        {
          id: 'places-areas',
          componentId: COMPONENT_IDS.AREA_LIST,
          variant: 'grid',
          values: { title: STYLE_PRESET_CONTENT.places_page_title },
        },
      ]),
    });
  }

  // ── Thème ──
  next.theme = {
    colors: options.brandColors ?? presetColors(presetId, sector),
    typography: { ...preset.styles.typography },
    borderStyle: preset.styles.borderStyle,
    decoration: preset.styles.decoration,
    verticalSpacing: preset.styles.verticalSpacing,
  };
  next.template = presetId;
  if (options.now) next.updateDate = options.now;
  return next;
}
